/**
 * Hook "alternatives": dato un materiale ancora, trova materiali simili
 * di altri fornitori (o stesso fornitore con prezzi diversi) per il
 * comparatore listino.
 *
 * Matching:
 *  1. Stesso `ean_code` (match perfetto, se valorizzato su entrambi)
 *  2. Stessa categoria + stessi attributi tecnici chiave:
 *     - board: material_type, board_type, thickness, sheet_thickness, width
 *     - screw: thickness (lunghezza)
 *     - structure_frame / structure_guide: width, sheet_thickness
 *     - insulation: thickness, density (se valorizzata)
 *     - other / accessory / finish: solo categoria (lista generica)
 *
 * Esclude il materiale ancora stesso e i materiali inattivi.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { DatabaseMaterial } from '@/hooks/useMaterials';
import { widthsMatch } from '@/lib/materials/taxonomy';

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export interface MaterialAlternative extends DatabaseMaterial {
  /** Differenza prezzo (€/unit) rispetto all'ancora. Negativo = più conveniente. */
  priceDeltaAbs: number | null;
  priceDeltaPct: number | null;
  /** Modalità di match riconosciuta. */
  matchKind: 'ean' | 'specs';
}

export function useMaterialAlternatives(anchor: DatabaseMaterial | null) {
  return useQuery<MaterialAlternative[]>({
    queryKey: ['material-alternatives', anchor?.id],
    enabled: !!anchor,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<MaterialAlternative[]> => {
      if (!anchor) return [];

      const anchorNet = num(anchor.net_price ?? anchor.unit_price ?? anchor.list_price);

      // Carichiamo tutti i candidati della stessa categoria (i numeri non sono enormi).
      // Il filtraggio tecnico avviene client-side per leggibilità.
      const { data, error } = await supabase
        // Prezzi NETTI dell'org corrente: da `materials` grezzo il campo
        // `net_price` non esiste e si cadeva su `unit_price`, che il trigger
        // di ricalcolo scrive con lo sconto di UNA organizzazione. Il confronto
        // "quanto risparmio con l'alternativa" usava quindi prezzi altrui.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('materials_with_pricing' as any)
        .select('*')
        .eq('category', anchor.category)
        .eq('is_active', true)
        .neq('id', anchor.id);
      if (error) throw error;

      const candidates = (data ?? []) as DatabaseMaterial[];
      const out: MaterialAlternative[] = [];

      for (const c of candidates) {
        // 1) EAN match — priorità massima se entrambi valorizzati
        const eanMatch =
          !!anchor.ean_code &&
          !!c.ean_code &&
          c.ean_code.trim() === anchor.ean_code.trim();

        // 2) Specs match per categoria
        let specsMatch = false;
        if (anchor.category === 'board') {
          specsMatch =
            (anchor.material_type ?? null) === (c.material_type ?? null) &&
            (anchor.board_type ?? null) === (c.board_type ?? null) &&
            num(anchor.thickness) === num(c.thickness) &&
            (anchor.sheet_thickness == null || num(anchor.sheet_thickness) === num(c.sheet_thickness));
        } else if (anchor.category === 'screw') {
          // Lunghezza vite memorizzata in thickness
          specsMatch = num(anchor.thickness) === num(c.thickness);
        } else if (anchor.category === 'structure_frame' || anchor.category === 'structure_guide') {
          // Larghezza a tolleranza: i profili della stessa misura nominale sono
          // scritti in modo diverso dai fornitori (montante 48, 49 o 50 per la
          // stessa parete da 50). Con l'uguaglianza esatta, i 65 profili a
          // misura "dispari" non avevano nessuna alternativa da confrontare.
          specsMatch =
            widthsMatch(anchor.width, c.width) &&
            num(anchor.sheet_thickness ?? anchor.thickness) === num(c.sheet_thickness ?? c.thickness);
        } else if (anchor.category === 'insulation') {
          specsMatch = num(anchor.thickness) === num(c.thickness);
        } else {
          // accessory / other / finish / ceiling_tile: confronto generico per categoria,
          // l'utente vede tutto e filtra a vista. Non includiamo qui per evitare rumore.
          specsMatch = false;
        }

        if (!eanMatch && !specsMatch) continue;

        const cNet = num(c.net_price ?? c.unit_price ?? c.list_price);
        const priceDeltaAbs = anchorNet != null && cNet != null ? cNet - anchorNet : null;
        const priceDeltaPct =
          anchorNet != null && anchorNet > 0 && cNet != null
            ? ((cNet - anchorNet) / anchorNet) * 100
            : null;

        out.push({
          ...c,
          matchKind: eanMatch ? 'ean' : 'specs',
          priceDeltaAbs,
          priceDeltaPct,
        });
      }

      // Ordina: prezzo più basso prima (più conveniente)
      out.sort((a, b) => {
        const aNet = num(a.net_price ?? a.unit_price ?? a.list_price) ?? Infinity;
        const bNet = num(b.net_price ?? b.unit_price ?? b.list_price) ?? Infinity;
        return aNet - bNet;
      });

      return out;
    },
  });
}
